import './Hero.scss';

import { cva, VariantProps } from 'class-variance-authority';
import classNames from 'classnames';
import { FC, ReactElement, ReactNode } from 'react';

import Body from '../Body';
import Button from '../Button';
import { ButtonProps } from '../Button/Button';
import { CtaButtons } from '../CtaButtons/CtaButtons';
import Title from '../Title';

const baseClassName = 'ds-Hero';

const heroPlacementVariance = cva(baseClassName, {
  variants: {
    placement: {
      left: 'asset-left',
      right: 'asset-right',
      above: 'asset-above',
      below: 'asset-below',
    },
  },
  defaultVariants: {
    placement: 'right',
  },
});

const heroRatioVariance = cva('hero-asset', {
  variants: {
    ratio: {
      square: 'ratio-square',
      wide: 'ratio-wide',
    },
  },
  defaultVariants: {
    ratio: 'wide',
  },
});

export type HeroProps = {
  Title: ReactElement<typeof Title>;
  Body: ReactElement<typeof Body>;
  PrimaryCta?: ReactElement<ButtonProps, typeof Button>;
  SecondaryCta?: ReactElement<ButtonProps, typeof Button>;
  className?: string;
  asset: {
    type: 'image' | 'embed';
    Component: ReactNode;
  } & VariantProps<typeof heroRatioVariance> &
    VariantProps<typeof heroPlacementVariance>;
};

export const Hero: FC<HeroProps> = (props) => {
  const { Title, Body, PrimaryCta, SecondaryCta, asset } = props;
  const { placement, type, ratio, Component } = asset;

  const renderAsset = () => {
    switch (type) {
      case 'image':
        return (
          <div className={classNames(heroRatioVariance({ ratio }))}>
            {Component}
          </div>
        );
      case 'embed':
        return <div>{props.asset.Component}</div>;
      default:
        return null;
    }
  };

  return (
    <section
      role="banner"
      aria-labelledby="hero-heading"
      className={classNames(heroPlacementVariance({ placement }))}
    >
      <div className="hero-content">
        {Title}
        {Body}
        <CtaButtons PrimaryCta={PrimaryCta} SecondaryCta={SecondaryCta} />
      </div>
      {renderAsset()}
    </section>
  );
};

export default Hero;
